import React, { useState, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
  AuroraPanelPoolRow,
  StructureRoofDetail,
} from '../../../utils/aurora-mapper.utils';
import { ConfirmationModal, ConfirmationType } from './ConfirmationModal';
import { PANEL_ORIENTATION_OPTIONS } from '../../../constants/app.constants';
import { PanelOrientation } from '../../../types';

interface AuroraPanelRowsReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (pool: AuroraPanelPoolRow[]) => void;
  initialPool: AuroraPanelPoolRow[];
  structures: StructureRoofDetail[];
  embedded?: boolean;
}

/**
 * Generates a unique ID for a new panel pool row.
 * @returns A unique string identifier
 */
const generatePoolRowId = (): string =>
  `apr_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

export const AuroraPanelRowsReviewModal: React.FC<AuroraPanelRowsReviewModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  initialPool,
  structures,
  embedded = false,
}) => {
  const [rows, setRows] = useState<AuroraPanelPoolRow[]>(() =>
    initialPool.map(r => ({ ...r })),
  );
  const [deleteTarget, setDeleteTarget] = useState<{ idx: number } | null>(null);

  // Re-sync when modal opens or initialPool changes
  React.useEffect(() => {
    if (isOpen) {
      setRows(initialPool.map(r => ({ ...r })));
      setDeleteTarget(null);
    }
  }, [initialPool, isOpen]);

  // Group rows by structure_index for display
  const groupedRows = useMemo(() => {
    const groups: { structureIndex: number; structureName: string; rows: { row: AuroraPanelPoolRow; globalIdx: number }[] }[] = [];

    // Build structure order from actual structures
    structures.forEach((s, idx) => {
      const structIdx = idx + 1;
      const structureRows: { row: AuroraPanelPoolRow; globalIdx: number }[] = [];
      rows.forEach((r, rIdx) => {
        if (r.structure_index === structIdx) structureRows.push({ row: r, globalIdx: rIdx });
      });
      if (structureRows.length > 0) {
        groups.push({ structureIndex: structIdx, structureName: s.structure_name, rows: structureRows });
      }
    });

    // Include rows pointing to structures beyond the current set (edge case)
    const coveredIndices = new Set(structures.map((_, i) => i + 1));
    rows.forEach((r, rIdx) => {
      if (!coveredIndices.has(r.structure_index)) {
        const existing = groups.find(g => g.structureIndex === r.structure_index);
        if (existing) {
          existing.rows.push({ row: r, globalIdx: rIdx });
        } else {
          groups.push({
            structureIndex: r.structure_index,
            structureName: `Structure ${r.structure_index}`,
            rows: [{ row: r, globalIdx: rIdx }],
          });
        }
      }
    });

    return groups;
  }, [rows, structures]);

  const totalPanels = useMemo(() => rows.reduce((sum, r) => sum + r.panel_count, 0), [rows]);

  const updateRow = useCallback((globalIdx: number, field: keyof AuroraPanelPoolRow, value: string | number) => {
    setRows(prev => prev.map((r, i) => i === globalIdx ? { ...r, [field]: value } : r));
  }, []);

  const handleDelete = useCallback(() => {
    if (deleteTarget === null) return;
    setRows(prev => prev.filter((_, i) => i !== deleteTarget.idx));
    setDeleteTarget(null);
  }, [deleteTarget]);

  const addRow = useCallback((structureIndex: number) => {
    const newRow: AuroraPanelPoolRow = {
      id: generatePoolRowId(),
      orientation: PanelOrientation.LANDSCAPE,
      panel_count: 1,
      structure_index: structureIndex,
    };
    setRows(prev => [...prev, newRow]);
  }, []);

  const canConfirm = useMemo(() => rows.every(r => r.panel_count >= 1), [rows]);

  const handleConfirm = useCallback(() => {
    if (canConfirm) onConfirm(rows);
  }, [rows, canConfirm, onConfirm]);

  if (!isOpen) return null;

  const isEmpty = rows.length === 0;

  // Structure options for reassignment dropdown
  const structureOptions = structures.map((s, idx) => ({
    value: idx + 1,
    label: s.structure_name || `Structure ${idx + 1}`,
  }));

  const content = (
    <>
      {/* Header */}
      <div className="px-5 pt-3 pb-3 3xl:px-6 3xl:pt-4 3xl:pb-4 border-b border-slate-100 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 3xl:w-10 3xl:h-10 rounded-xl bg-gradient-to-br from-violet-600 to-violet-700 flex items-center justify-center shadow-sm">
              <svg className="w-5 h-5 3xl:w-6 3xl:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
            </div>
            <div>
              <h3 className="text-sm 3xl:text-lg font-semibold text-slate-800">Panel Rows Review</h3>
              <p className="text-[11px] 3xl:text-sm text-slate-400">Review and adjust panel rows per structure</p>
            </div>
          </div>
          {!isEmpty && (
            <div className="text-center">
              <div className="text-[10px] 3xl:text-sm uppercase tracking-wider font-semibold text-slate-400">Total Panels</div>
              <div className="text-sm 3xl:text-lg font-mono font-semibold text-violet-600">{totalPanels}</div>
            </div>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 px-5 py-4 3xl:px-6 3xl:py-5 overflow-y-auto">
        {isEmpty ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="w-12 h-12 3xl:w-14 3xl:h-14 rounded-xl bg-slate-100 flex items-center justify-center mb-3">
              <svg className="w-6 h-6 3xl:w-7 3xl:h-7 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
            </div>
            <p className="text-sm 3xl:text-lg font-medium text-slate-600">No panel rows extracted</p>
            <p className="text-[11px] 3xl:text-sm text-slate-400 mt-1">Add rows manually using the buttons below.</p>
            {structures.length > 0 && (
              <div className="flex gap-2 mt-4 flex-wrap justify-center">
                {structures.map((s, idx) => (
                  <button
                    key={idx}
                    onClick={() => addRow(idx + 1)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-violet-700 bg-violet-50 hover:bg-violet-100 border border-violet-200 rounded-lg transition-colors"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                    </svg>
                    Add to {s.structure_name || `Structure ${idx + 1}`}
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-5 3xl:space-y-6">
            {groupedRows.map(({ structureIndex, structureName, rows: structureRows }) => (
              <div key={structureIndex} className="rounded-xl border border-slate-200 border-l-[3px] border-l-violet-500 overflow-hidden">
                {/* Structure group header */}
                <div className="px-4 py-2.5 3xl:px-5 3xl:py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <svg className="w-3.5 h-3.5 text-violet-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10l9-7 9 7M4 10v10h6v-6h4v6h6V10" />
                    </svg>
                    <h4 className="text-xs 3xl:text-base font-bold uppercase tracking-wider text-slate-700">
                      {structureName}
                    </h4>
                  </div>
                  <div className="flex items-center gap-1.5 px-2.5 py-1 bg-slate-100/80 border border-slate-200/60 rounded-md">
                    <span className="text-[9px] 3xl:text-xs font-semibold uppercase tracking-wider text-slate-500">Panels</span>
                    <span className="text-[11px] 3xl:text-sm font-mono font-semibold text-slate-700">
                      {structureRows.reduce((sum, { row }) => sum + row.panel_count, 0)}
                    </span>
                  </div>
                </div>

                {/* Rows table */}
                <div className="overflow-x-auto">
                  <table className="w-full text-xs 3xl:text-sm">
                    <thead>
                      <tr className="bg-slate-50/50">
                        <th className="px-2 py-1.5 3xl:px-3 3xl:py-2 text-left text-[9px] 3xl:text-xs font-semibold uppercase tracking-wider text-slate-400 w-8">#</th>
                        <th className="px-2 py-1.5 3xl:px-3 3xl:py-2 text-left text-[9px] 3xl:text-xs font-semibold uppercase tracking-wider text-slate-400">Orientation</th>
                        <th className="px-2 py-1.5 3xl:px-3 3xl:py-2 text-left text-[9px] 3xl:text-xs font-semibold uppercase tracking-wider text-slate-400">Panel Count</th>
                        <th className="px-2 py-1.5 3xl:px-3 3xl:py-2 text-left text-[9px] 3xl:text-xs font-semibold uppercase tracking-wider text-slate-400">Structure</th>
                        <th className="px-2 py-1.5 3xl:px-3 3xl:py-2 text-right text-[9px] 3xl:text-xs font-semibold uppercase tracking-wider text-slate-400 w-12" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {structureRows.map(({ row, globalIdx }, localIdx) => (
                        <tr key={row.id} className="hover:bg-violet-50/30 transition-colors">
                          <td className="px-2 py-1.5 3xl:px-3 3xl:py-2 text-[9px] 3xl:text-xs font-mono text-slate-400">
                            {localIdx + 1}
                          </td>
                          <td className="px-2 py-1.5 3xl:px-3 3xl:py-2">
                            <select
                              value={row.orientation}
                              onChange={e => updateRow(globalIdx, 'orientation', e.target.value as PanelOrientation)}
                              className="px-1.5 py-0.5 3xl:px-2 3xl:py-1 text-xs 3xl:text-sm border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-violet-500 focus:border-violet-500 bg-white"
                            >
                              {PANEL_ORIENTATION_OPTIONS.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                            </select>
                          </td>
                          <td className="px-2 py-1.5 3xl:px-3 3xl:py-2">
                            <input
                              type="number"
                              min={1}
                              value={row.panel_count}
                              onChange={e => {
                                const v = parseInt(e.target.value, 10);
                                if (!isNaN(v)) updateRow(globalIdx, 'panel_count', v);
                              }}
                              className={`w-20 px-1.5 py-0.5 3xl:px-2 3xl:py-1 text-xs 3xl:text-sm font-mono border rounded focus:outline-none focus:ring-1 focus:ring-violet-500 focus:border-violet-500 ${
                                row.panel_count < 1 ? 'border-red-300 bg-red-50' : 'border-slate-200'
                              }`}
                            />
                          </td>
                          <td className="px-2 py-1.5 3xl:px-3 3xl:py-2">
                            <select
                              value={row.structure_index}
                              onChange={e => updateRow(globalIdx, 'structure_index', parseInt(e.target.value, 10))}
                              className="px-1.5 py-0.5 3xl:px-2 3xl:py-1 text-xs 3xl:text-sm border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-violet-500 focus:border-violet-500 bg-white"
                            >
                              {structureOptions.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                            </select>
                          </td>
                          <td className="px-2 py-1.5 3xl:px-3 3xl:py-2 text-right">
                            <button
                              onClick={() => setDeleteTarget({ idx: globalIdx })}
                              className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                              title="Delete row"
                            >
                              <svg className="w-3.5 h-3.5 3xl:w-4 3xl:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Add Row button */}
                <div className="px-4 py-2 3xl:px-5 3xl:py-2.5 border-t border-slate-100">
                  <button
                    onClick={() => addRow(structureIndex)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] 3xl:text-xs font-medium text-violet-700 hover:bg-violet-50 rounded-lg transition-colors"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                    </svg>
                    Add Row
                  </button>
                </div>
              </div>
            ))}

            {/* Add row for structures with no rows yet */}
            {structures
              .filter((_, idx) => !groupedRows.some(g => g.structureIndex === idx + 1))
              .map((s, idx) => {
                const actualIdx = structures.indexOf(s);
                return (
                  <div key={actualIdx} className="rounded-xl border border-dashed border-slate-300 px-4 py-3 3xl:px-5 3xl:py-4 flex items-center justify-between">
                    <span className="text-xs 3xl:text-sm text-slate-500">
                      {s.structure_name || `Structure ${actualIdx + 1}`} — No panel rows
                    </span>
                    <button
                      onClick={() => addRow(actualIdx + 1)}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] 3xl:text-xs font-medium text-violet-700 bg-violet-50 hover:bg-violet-100 border border-violet-200 rounded-lg transition-colors"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                      </svg>
                      Add Row
                    </button>
                  </div>
                );
              })}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-5 py-3.5 3xl:px-6 3xl:py-4 border-t border-slate-100 flex items-center justify-between flex-shrink-0">
        <p className="text-[10px] 3xl:text-sm text-slate-400">
          {isEmpty
            ? 'No panel rows to review — add rows or continue'
            : 'Review panel counts, orientations, and structure assignments, then confirm'}
        </p>
        <div className="flex gap-2.5">
          <button
            onClick={onClose}
            className="px-3.5 py-1.5 3xl:px-4 3xl:py-2 text-xs 3xl:text-base font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-50 rounded-lg border border-slate-200 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!canConfirm && !isEmpty}
            className={`px-3.5 py-1.5 3xl:px-4 3xl:py-2 text-xs 3xl:text-base font-medium rounded-lg shadow-sm transition-all duration-150 ${
              canConfirm || isEmpty
                ? 'text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 shadow-emerald-500/20'
                : 'text-slate-400 bg-slate-100 cursor-not-allowed shadow-none'
            }`}
          >
            {isEmpty ? 'Continue' : 'Confirm Panel Rows'}
          </button>
        </div>
      </div>

      {/* Delete confirmation */}
      <ConfirmationModal
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete Panel Row"
        message="Delete this panel row? This action cannot be undone."
        type={ConfirmationType.DANGER}
        confirmButtonText="Delete"
        cancelButtonText="Keep"
      />
    </>
  );

  if (embedded) return content;

  return createPortal(
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-5xl 3xl:max-w-7xl mx-auto shadow-2xl border border-gray-100 overflow-hidden flex flex-col h-[85vh]">
        {content}
      </div>
    </div>,
    document.body,
  );
};
